import { useRef, useState } from 'react';

import { XStack, YStack } from '@/components/ui/layout/layout';

export function CredentialOtpInput({
  id,
  label,
  length,
  slots,
  onChange,
}: {
  id: string;
  label: string;
  length: number;
  slots: string[];
  onChange: (slots: string[]) => void;
}) {
  const inputs = useRef<(HTMLInputElement | null)[]>([]);
  const [error, setError] = useState(false);
  const focus = (index: number) => {
    const input = inputs.current[Math.max(0, Math.min(length - 1, index))];
    input?.focus();
    input?.select();
  };
  const update = (index: number, text: string) => {
    // Full-code autofill can arrive at any slot. Partial pastes start at focus
    const start = text.length === length ? 0 : index;
    if (
      (text && !/^[A-Za-z0-9]+$/.test(text)) ||
      start + text.length > length
    ) {
      setError(true);
      return;
    }
    const next = Array.from({ length }, (_, position) => slots[position] ?? '');
    if (!text) next[index] = '';
    else
      Array.from(text).forEach((char, offset) => {
        next[start + offset] = char;
      });
    onChange(next);
    setError(false);
    if (text) focus(start + text.length);
  };

  return (
    <YStack className="gap-2">
      <XStack role="group" aria-label={label} className="gap-1.5 sm:gap-2">
        {Array.from({ length }, (_, index) => (
          <input
            key={index}
            ref={(input) => {
              inputs.current[index] = input;
            }}
            id={index === 0 ? id : `${id}-${index}`}
            aria-label={`${label}, character ${index + 1} of ${length}`}
            aria-invalid={error || undefined}
            aria-describedby={error ? `${id}-error` : undefined}
            type="text"
            autoComplete={index === 0 ? 'one-time-code' : 'off'}
            autoCapitalize="off"
            spellCheck={false}
            required
            pattern="[A-Za-z0-9]"
            value={slots[index] ?? ''}
            onFocus={(event) => event.target.select()}
            onChange={(event) => update(index, event.target.value)}
            onPaste={(event) => {
              event.preventDefault();
              update(index, event.clipboardData.getData('text'));
            }}
            onKeyDown={(event) => {
              if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
                event.preventDefault();
                focus(index + (event.key === 'ArrowLeft' ? -1 : 1));
              } else if (event.key === 'Backspace') {
                event.preventDefault();
                const target = slots[index] ? index : Math.max(0, index - 1);
                update(target, '');
                focus(target);
              } else if (event.key === 'Enter') {
                event.preventDefault();
              }
            }}
            className="h-11 min-w-0 w-9 rounded-lg border border-border bg-background text-center text-base text-foreground outline-none focus-visible:ring-2 focus-visible:ring-accent sm:w-10"
          />
        ))}
      </XStack>
      {error ? (
        <p id={`${id}-error`} role="alert" className="text-sm text-danger-11">
          Enter up to {length} letters or numbers without spaces.
        </p>
      ) : null}
    </YStack>
  );
}
